/**
 * Shared SVG style contracts for the 2D plan, as labeled Emotion css
 * constants applied via the css prop.
 *
 * These are contracts, not conveniences: `EDGE_SEL_STYLE` is THE selected-edge
 * style (rendered only by the shared EdgeSel component); `HANDLE_STYLE` /
 * `EDGE_HIT_STYLE` / `ALCOVE_EDGE_HIT_STYLE` are the uniform affordance +
 * hit-strip styles every plan component shares.
 */
import { css } from '@emotion/react';
import { accentSoft, dimColor, handleStroke, selection, white } from '../../styles';

/** The invisible hit polygon of a wall run: from just inside the inner line out across the band (WallHitStrip). */
export const WALL_HIT_STYLE = css`
  label: WALL_HIT_STYLE;
  fill: transparent;
  stroke: none;
`;

/** Fat invisible hit strip along a primary edge / chamfer diagonal. */
export const EDGE_HIT_STYLE = css`
  label: EDGE_HIT_STYLE;
  stroke: transparent;
  stroke-width: 14;
  fill: none;
`;

/** Square drag handle: white fill, ink chrome outline (not selection). */
export const HANDLE_STYLE = css`
  label: HANDLE_STYLE;
  fill: ${white};
  stroke: ${handleStroke};
  stroke-width: 2;
  cursor: grab;
  &:hover { fill: ${accentSoft}; }
`;

export const HANDLE_CORNER_STYLE = css`
  label: HANDLE_CORNER_STYLE;
  cursor: nwse-resize;
`;

/** Clickable/movable alcove interior. */
export const ALCOVE_BODY_STYLE = css`
  label: ALCOVE_BODY_STYLE;
  fill: transparent;
  cursor: move;
`;

/**
 * THE selected-edge style, rendered only by the shared EdgeSel component
 * (handles.tsx): a single selection-color line, no outline. Never restyle a
 * selected edge anywhere else.
 */
export const EDGE_SEL_STYLE = css`
  label: EDGE_SEL_STYLE;
  fill: none;
  pointer-events: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke: ${selection};
  stroke-width: 4;
`;

/** Fat invisible hit strip along an alcove sub-wall. */
export const ALCOVE_EDGE_HIT_STYLE = css`
  label: ALCOVE_EDGE_HIT_STYLE;
  stroke: transparent;
  stroke-width: 16;
  fill: none;
`;

export const DIM_LINE_STYLE = css`
  label: DIM_LINE_STYLE;
  stroke: ${dimColor};
  stroke-width: 1;
`;

export const DIM_TICK_STYLE = css`
  label: DIM_TICK_STYLE;
  stroke: ${dimColor};
  stroke-width: 1.5;
`;

/** Bold variants for an emphasized bar — the center (size) measurement of a gap/size/gap triple. */
export const DIM_LINE_EMPHASIZED_STYLE = css`
  label: DIM_LINE_EMPHASIZED_STYLE;
  stroke: ${dimColor};
  stroke-width: 2.5;
`;

export const DIM_TICK_EMPHASIZED_STYLE = css`
  label: DIM_TICK_EMPHASIZED_STYLE;
  stroke: ${dimColor};
  stroke-width: 3;
`;
